// "You asked / Claude did", collapsed by default. Adapted from EveryInc/tend
// src/feed/CardView.tsx CardHistory (MIT, Copyright (c) 2026 Every, Inc.).
import { useState } from "react";
import { ChevronRight } from "lucide-react";
import type { CardHistoryEntry } from "@shared/core";
import { cn } from "@web/lib/cn";
import { formatFull, formatListTime } from "@web/lib/time";
import { readableHistory } from "../wording";

export function CardHistory({
  entries,
  now,
}: {
  entries: readonly CardHistoryEntry[];
  now: number;
}) {
  const [open, setOpen] = useState(false);
  const lines = readableHistory(entries);
  if (!lines.length) return null;
  return (
    <section className="border-t border-line bg-bg/60 px-5 py-2.5 md:px-7">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-sm text-muted hover:text-ink"
      >
        <ChevronRight
          aria-hidden
          className={cn("size-3.5 transition-transform", open && "rotate-90")}
        />
        History · {lines.length}
      </button>
      {open ? (
        <ol className="mt-2 flex flex-col gap-1.5 pb-1">
          {lines.map((line, i) => (
            <li
              key={`${line.at}-${i}`}
              className="grid grid-cols-[7.5rem_minmax(0,1fr)_auto] gap-2 text-sm"
            >
              <b
                className={cn("mm-kicker pt-0.5 !text-[10px]", line.attention && "!text-note-ink")}
              >
                {line.label}
              </b>
              <span className="min-w-0 break-words text-ink-2">{line.detail}</span>
              <time dateTime={line.at} title={formatFull(line.at)} className="text-xs text-faint">
                {formatListTime(line.at, now)}
              </time>
            </li>
          ))}
        </ol>
      ) : null}
    </section>
  );
}
